/**
 * Live Caption Job Monitoring Types
 * Shared between client and server for type-safe API contracts
 */

// ============================================================================
// Caption Job Status
// ============================================================================

/** Possible caption statuses derived from pipeline analysis */
export type CaptionJobStatus = 'captioned' | 'missing' | 'pending';

/** Status values from liveEventAutoCaption that indicate the session has ended */
export const LEAC_ENDED_STATUSES = ['ended', 'end', 'End of Transcribe'] as const;

/** Status values from liveEventAutoCaption that indicate the session is active */
export const LEAC_ACTIVE_STATUSES = ['starting', 'live', 'running'] as const;

// ============================================================================
// Query Parameters
// ============================================================================

export interface CaptionJobsQueryParams {
  clientID?: string;
  startDate?: string;
  endDate?: string;
  status?: CaptionJobStatus;
  page?: string;
  limit?: string;
}

// ============================================================================
// Response Types
// ============================================================================

export interface CaptionJobRow {
  /** liveEventAutoCaption.lecid */
  lecid: number;
  /** Full event ID (clientID_eventSuffix) */
  eventID: string;
  /** Client ID */
  clientID: string;
  /** Client name */
  clientName: string;
  /** liveEventAutoCaption status */
  leacStatus: string;
  /** When the live caption session started */
  sessionStart: string;
  /** Duration of live caption session in seconds */
  captionDuration: number | null;
  /** Whether the event has a published (archive) video asset */
  isPublished: boolean;
  /** When the video was published (archive dateCreated) */
  publishedAt: string | null;
  /** Whether a caption VTT exists in mediaassets */
  hasCaptionVtt: boolean;
  /** Caption VTT file size in bytes */
  captionFileSize: number | null;
  /** When the caption VTT was created */
  captionCreatedAt: string | null;
  /** URL path to the caption VTT (for analyzer link) */
  captionPath: string | null;
  /** Derived status for display */
  status: CaptionJobStatus;
  /** Seconds between publish and caption creation */
  timeToCaptionSeconds: number | null;
}

export interface CaptionJobsResponse {
  jobs: CaptionJobRow[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };
  summary: {
    total: number;
    captioned: number;
    missing: number;
    pending: number;
  };
  cacheInfo?: {
    cachedAt: string;
    age: number;
    ttl: number;
    source: 'cache' | 'fresh';
  };
}

// ============================================================================
// Config
// ============================================================================

export const CAPTION_JOBS_CONFIG = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 25,
  MAX_LIMIT: 100,
  MAX_DATE_RANGE_DAYS: 90,
  /** Default lookback in hours when no date range specified */
  DEFAULT_LOOKBACK_HOURS: 72,
} as const;

// ============================================================================
// VTT Quality Score (batch analysis)
// ============================================================================

/** Quality severity label */
export type VttQualitySeverity = 'good' | 'fair' | 'poor';

/** Quality score for a single caption job's VTT file */
export interface VttQualityScore {
  /** Composite quality score 0-100 (100 = best) */
  score: number;
  /** Severity label */
  severity: VttQualitySeverity;
  /** Raw hallucination score from VTT analyzer (0-100, higher = worse) */
  hallucinationScore: number;
  /** VTT duration vs session duration ratio (0-1, 1 = full coverage) */
  coverageRatio: number | null;
  /** Whether this result came from cache */
  cached: boolean;
}

/** Request body for batch VTT analysis */
export interface VttBatchAnalyzeRequest {
  /** Array of items to analyze */
  items: Array<{
    eventID: string;
    captionPath: string;
    captionDuration: number | null;
  }>;
}

/** Response from batch VTT analysis */
export interface VttBatchAnalyzeResponse {
  /** Score results keyed by eventID */
  scores: Record<string, VttQualityScore>;
  /** Number of items that were cache hits */
  cacheHits: number;
  /** Number of items that required fresh analysis */
  freshAnalyses: number;
  /** Any eventIDs that failed analysis */
  errors: Record<string, string>;
}
